import ChevronDownIcon from '@persona-id/design-tokens/icons/ChevronDownIcon';
import { Control, Controller, UseFormRegister } from 'react-hook-form';
import styled from 'styled-components';

import FeatureFlagged from 'dashboard/components/FeatureFlagged';
import ReactSelect, { OptionType } from 'dashboard/components/ReactSelect';
import { ImporterArraySyncMode, ImporterSyncMode } from 'dashboard/models/importer';
import { NewImporterFormData } from 'dashboard/views/DashboardImporterStepperView/DashboardImporterStepperView';
import StepperCard from 'dashboard/views/DashboardImporterStepperView/StepperCard';
import StepperFormControlLabel from 'dashboard/views/DashboardImporterStepperView/StepperFormControlLabel';
import Button from 'design-system/components/Button';
import Radio from 'design-system/components/Radio';
import RadioGroup from 'design-system/components/RadioGroup';
import Spacer from 'design-system/components/Spacer';

const SelectContainer = styled.div``;

const SectionColumn = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0px, 1fr));
`;

interface SetDestinationStepProps {
  canAdvance: boolean;
  control: Control<NewImporterFormData, object>;
  register: UseFormRegister<NewImporterFormData>;
  destination: 'Account' | 'Txn';
  destinationOptions: OptionType<'Account' | 'Txn'>[];
  destinationTypeId: string;
  destinationTypeIdOptions: { label: string; value: string }[];
  syncMode: ImporterSyncMode;
  arraySyncMode: ImporterArraySyncMode;
  isActive: boolean;
  isSubmitting: boolean;
  isValid: boolean;
  handleCardClick: () => void;
}
const SetDestinationStep = ({
  canAdvance,
  control,
  register,
  destination,
  destinationOptions,
  destinationTypeId,
  destinationTypeIdOptions,
  syncMode,
  isActive,
  isSubmitting,
  isValid,
  handleCardClick,
}: SetDestinationStepProps) => {
  const destinationName = destination
    ? destination === 'Account'
      ? 'Account'
      : 'Transaction'
    : 'Object';
  const syncModeOptions: OptionType<ImporterSyncMode>[] = [
    { label: `Create new or update existing ${destinationName}s`, value: ImporterSyncMode.Upsert },
    { label: `Create new ${destinationName}s only`, value: ImporterSyncMode.Insert },
    { label: `Update existing ${destinationName}s only`, value: ImporterSyncMode.Update },
  ];

  const arraySyncModeOptions: OptionType<ImporterArraySyncMode>[] = [
    { label: 'Append to existing array', value: ImporterArraySyncMode.Append },
    { label: 'Replace entire array', value: ImporterArraySyncMode.Replace },
  ];

  return (
    <StepperCard
      id='importer-stepper-panel__setup-importer'
      title='3. Set destination'
      onClick={() => handleCardClick()}
      actions={
        isActive ? (
          <Button type='submit' variant='basic' disabled={!canAdvance || !isValid || isSubmitting}>
            Create
          </Button>
        ) : (
          <ChevronDownIcon onClick={handleCardClick} />
        )
      }
      active={isActive}
    >
      <SectionColumn>
        <SelectContainer>
          <StepperFormControlLabel htmlFor='destination'>Destination</StepperFormControlLabel>
          <Controller
            control={control}
            defaultValue={destination}
            name='destination'
            render={({ field: { onChange, value } }) => {
              return (
                <ReactSelect
                  id='dashboard-importer-view__destination'
                  aria-label='Destination'
                  isClearable={false}
                  value={value}
                  onChange={onChange}
                  options={destinationOptions}
                  placeholder='Destination'
                  portal={true}
                />
              );
            }}
          />
          <Spacer height={2} />
          <StepperFormControlLabel htmlFor='destinationTypeId'>
            Destination Type
          </StepperFormControlLabel>
          <Controller
            control={control}
            defaultValue={destinationTypeId}
            name='destinationTypeId'
            render={({ field: { onChange, value } }) => {
              return (
                <ReactSelect
                  disabled={destination == null}
                  id='dashboard-importer-view__destination-type-id'
                  aria-FormControlLabel='Destination Type'
                  isClearable={false}
                  value={value}
                  onChange={onChange}
                  options={destinationTypeIdOptions}
                  placeholder='Destination Type'
                  portal={true}
                />
              );
            }}
          />
        </SelectContainer>
      </SectionColumn>
      <Spacer height={2} />
      <StepperFormControlLabel htmlFor='syncMode'>
        Please indicate what action you&apos;d like to take
      </StepperFormControlLabel>
      <RadioGroup>
        {syncModeOptions.map((option) => {
          return (
            <Radio
              key={option.value}
              label={option.label}
              {...register('syncMode')}
              value={option.value}
            />
          );
        })}
      </RadioGroup>
      <FeatureFlagged featureFlag='dashboard:importers-enable-csv-arrays-using-json'>
        <Spacer height={2} />
        <StepperFormControlLabel htmlFor='arraySyncMode'>
          Array column behavior
        </StepperFormControlLabel>
        <RadioGroup>
          {arraySyncModeOptions.map((option) => {
            return (
              <Radio
                key={option.value}
                label={option.label}
                {...register('arraySyncMode')}
                value={option.value}
                disabled={syncMode === ImporterSyncMode.Insert}
              />
            );
          })}
        </RadioGroup>
      </FeatureFlagged>
    </StepperCard>
  );
};

export default SetDestinationStep;
